import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import workflowService from '../../services/workflowService';

// Initial state
const initialState = {
  // Workflows list (admin view)
  workflows: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,

  // Current workflow being edited/viewed
  currentWorkflow: null,
  currentWorkflowSteps: [],
  currentWorkflowRules: [],

  // Workflow versions
  workflowVersions: [],
  activeVersion: null,

  // Reference data for forms
  roles: [],
  userGroups: [],

  // UI state
  isLoading: false,
  isSaving: false,
  isPublishing: false,
  error: null,
  successMessage: null,

  // Designer state
  isDirty: false, // Has unsaved changes
  selectedStepId: null,
};

// ==================== Async Thunks ====================

/**
 * Fetch all workflows with optional filters
 */
export const fetchWorkflows = createAsyncThunk(
  'workflow/fetchWorkflows',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await workflowService.getWorkflows(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch workflows' });
    }
  }
);

/**
 * Fetch single workflow detail
 */
export const fetchWorkflowDetail = createAsyncThunk(
  'workflow/fetchWorkflowDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await workflowService.getWorkflowDetail(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch workflow detail' });
    }
  }
);

/**
 * Create new workflow
 */
export const createWorkflow = createAsyncThunk(
  'workflow/createWorkflow',
  async (data, { rejectWithValue }) => {
    try {
      const response = await workflowService.createWorkflow(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to create workflow' });
    }
  }
);

/**
 * Update workflow
 */
export const updateWorkflow = createAsyncThunk(
  'workflow/updateWorkflow',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await workflowService.updateWorkflow(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update workflow' });
    }
  }
);

/**
 * Delete workflow
 */
export const deleteWorkflow = createAsyncThunk(
  'workflow/deleteWorkflow',
  async (id, { rejectWithValue }) => {
    try {
      await workflowService.deleteWorkflow(id);
      return { id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to delete workflow' });
    }
  }
);

/**
 * Publish workflow version
 */
export const publishWorkflow = createAsyncThunk(
  'workflow/publishWorkflow',
  async (id, { rejectWithValue }) => {
    try {
      const response = await workflowService.publishWorkflow(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to publish workflow' });
    }
  }
);

/**
 * Clone workflow
 */
export const cloneWorkflow = createAsyncThunk(
  'workflow/cloneWorkflow',
  async (id, { rejectWithValue }) => {
    try {
      const response = await workflowService.cloneWorkflow(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to clone workflow' });
    }
  }
);

// ==================== Workflow Steps ====================

/**
 * Fetch workflow steps
 */
export const fetchWorkflowSteps = createAsyncThunk(
  'workflow/fetchWorkflowSteps',
  async (workflowVersionId, { rejectWithValue }) => {
    try {
      const response = await workflowService.getWorkflowSteps({ workflow_version: workflowVersionId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch workflow steps' });
    }
  }
);

/**
 * Create workflow step
 */
export const createWorkflowStep = createAsyncThunk(
  'workflow/createWorkflowStep',
  async (data, { rejectWithValue }) => {
    try {
      const response = await workflowService.createWorkflowStep(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to create workflow step' });
    }
  }
);

/**
 * Update workflow step
 */
export const updateWorkflowStep = createAsyncThunk(
  'workflow/updateWorkflowStep',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await workflowService.updateWorkflowStep(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update workflow step' });
    }
  }
);

/**
 * Delete workflow step
 */
export const deleteWorkflowStep = createAsyncThunk(
  'workflow/deleteWorkflowStep',
  async (id, { rejectWithValue }) => {
    try {
      await workflowService.deleteWorkflowStep(id);
      return { id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to delete workflow step' });
    }
  }
);

// ==================== Workflow Rules ====================

/**
 * Fetch workflow rules
 */
export const fetchWorkflowRules = createAsyncThunk(
  'workflow/fetchWorkflowRules',
  async (workflowId, { rejectWithValue }) => {
    try {
      const response = await workflowService.getWorkflowRules({ workflow: workflowId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch workflow rules' });
    }
  }
);

/**
 * Create workflow rule
 */
export const createWorkflowRule = createAsyncThunk(
  'workflow/createWorkflowRule',
  async (data, { rejectWithValue }) => {
    try {
      const response = await workflowService.createWorkflowRule(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to create workflow rule' });
    }
  }
);

/**
 * Update workflow rule
 */
export const updateWorkflowRule = createAsyncThunk(
  'workflow/updateWorkflowRule',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await workflowService.updateWorkflowRule(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update workflow rule' });
    }
  }
);

/**
 * Delete workflow rule
 */
export const deleteWorkflowRule = createAsyncThunk(
  'workflow/deleteWorkflowRule',
  async (id, { rejectWithValue }) => {
    try {
      await workflowService.deleteWorkflowRule(id);
      return { id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to delete workflow rule' });
    }
  }
);

// ==================== Reference Data ====================

/**
 * Fetch roles for approver assignment
 */
export const fetchRoles = createAsyncThunk(
  'workflow/fetchRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await workflowService.getRoles();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch roles' });
    }
  }
);

/**
 * Fetch user groups for approver assignment
 */
export const fetchUserGroups = createAsyncThunk(
  'workflow/fetchUserGroups',
  async (_, { rejectWithValue }) => {
    try {
      const response = await workflowService.getUserGroups();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch user groups' });
    }
  }
);

// ==================== Slice ====================

const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    // Clear current workflow
    clearCurrentWorkflow: (state) => {
      state.currentWorkflow = null;
      state.currentWorkflowSteps = [];
      state.currentWorkflowRules = [];
      state.isDirty = false;
      state.selectedStepId = null;
    },

    // Set selected step
    setSelectedStepId: (state, action) => {
      state.selectedStepId = action.payload;
    },

    // Mark as dirty (has unsaved changes)
    setIsDirty: (state, action) => {
      state.isDirty = action.payload;
    },

    // Clear error messages
    clearError: (state) => {
      state.error = null;
    },

    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    // Set success message
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },

    // Update local step data (for designer)
    updateLocalStep: (state, action) => {
      const { stepId, data } = action.payload;
      const index = state.currentWorkflowSteps.findIndex(
        step => step.id?.toString() === stepId?.toString()
      );
      if (index !== -1) {
        state.currentWorkflowSteps[index] = { ...state.currentWorkflowSteps[index], ...data };
        state.isDirty = true;
      }
    },

    // Add local step (for designer before save)
    addLocalStep: (state, action) => {
      state.currentWorkflowSteps.push(action.payload);
      state.isDirty = true;
    },

    // Remove local step
    removeLocalStep: (state, action) => {
      state.currentWorkflowSteps = state.currentWorkflowSteps.filter(
        step => step.id?.toString() !== action.payload?.toString()
      );
      state.selectedStepId = null; // Clear selection when removing step
      state.isDirty = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // ==================== Fetch Workflows ====================
      .addCase(fetchWorkflows.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkflows.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workflows = action.payload.results || action.payload;
        state.totalCount = action.payload.count || action.payload.length;
      })
      .addCase(fetchWorkflows.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Fetch Workflow Detail ====================
      .addCase(fetchWorkflowDetail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowDetail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentWorkflow = action.payload;
        state.workflowVersions = action.payload.versions || [];
        state.activeVersion = action.payload.versions?.find(v => v.is_active) || null;
      })
      .addCase(fetchWorkflowDetail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Create Workflow ====================
      .addCase(createWorkflow.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createWorkflow.fulfilled, (state, action) => {
        state.isSaving = false;
        state.currentWorkflow = action.payload;
        state.successMessage = 'Workflow created successfully';
        state.isDirty = false;
      })
      .addCase(createWorkflow.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // ==================== Update Workflow ====================
      .addCase(updateWorkflow.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateWorkflow.fulfilled, (state, action) => {
        state.isSaving = false;
        state.currentWorkflow = action.payload;
        state.successMessage = 'Workflow updated successfully';
        state.isDirty = false;
      })
      .addCase(updateWorkflow.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // ==================== Delete Workflow ====================
      .addCase(deleteWorkflow.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteWorkflow.fulfilled, (state, action) => {
        state.isSaving = false;
        state.workflows = state.workflows.filter(w => w.id !== action.payload.id);
        state.successMessage = 'Workflow deleted successfully';
      })
      .addCase(deleteWorkflow.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // ==================== Publish Workflow ====================
      .addCase(publishWorkflow.pending, (state) => {
        state.isPublishing = true;
        state.error = null;
      })
      .addCase(publishWorkflow.fulfilled, (state, action) => {
        state.isPublishing = false;
        state.currentWorkflow = action.payload;
        state.successMessage = 'Workflow published successfully';
      })
      .addCase(publishWorkflow.rejected, (state, action) => {
        state.isPublishing = false;
        state.error = action.payload;
      })

      // ==================== Clone Workflow ====================
      .addCase(cloneWorkflow.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(cloneWorkflow.fulfilled, (state, action) => {
        state.isSaving = false;
        state.workflows.push(action.payload);
        state.successMessage = 'Workflow cloned successfully';
      })
      .addCase(cloneWorkflow.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // ==================== Fetch Workflow Steps ====================
      .addCase(fetchWorkflowSteps.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowSteps.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentWorkflowSteps = action.payload.results || action.payload;
      })
      .addCase(fetchWorkflowSteps.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Create Workflow Step ====================
      .addCase(createWorkflowStep.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createWorkflowStep.fulfilled, (state, action) => {
        state.isSaving = false;
        state.currentWorkflowSteps.push(action.payload);
        state.successMessage = 'Step created successfully';
      })
      .addCase(createWorkflowStep.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // ==================== Update Workflow Step ====================
      .addCase(updateWorkflowStep.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateWorkflowStep.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.currentWorkflowSteps.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.currentWorkflowSteps[index] = action.payload;
        }
        state.successMessage = 'Step updated successfully';
      })
      .addCase(updateWorkflowStep.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // ==================== Delete Workflow Step ====================
      .addCase(deleteWorkflowStep.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteWorkflowStep.fulfilled, (state, action) => {
        state.isSaving = false;
        state.currentWorkflowSteps = state.currentWorkflowSteps.filter(
          s => s.id !== action.payload.id
        );
        state.successMessage = 'Step deleted successfully';
      })
      .addCase(deleteWorkflowStep.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // ==================== Fetch Workflow Rules ====================
      .addCase(fetchWorkflowRules.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowRules.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentWorkflowRules = action.payload.results || action.payload;
      })
      .addCase(fetchWorkflowRules.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Create Workflow Rule ====================
      .addCase(createWorkflowRule.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createWorkflowRule.fulfilled, (state, action) => {
        state.isSaving = false;
        state.currentWorkflowRules.push(action.payload);
        state.successMessage = 'Rule created successfully';
      })
      .addCase(createWorkflowRule.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // ==================== Update Workflow Rule ====================
      .addCase(updateWorkflowRule.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateWorkflowRule.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.currentWorkflowRules.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.currentWorkflowRules[index] = action.payload;
        }
        state.successMessage = 'Rule updated successfully';
      })
      .addCase(updateWorkflowRule.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // ==================== Delete Workflow Rule ====================
      .addCase(deleteWorkflowRule.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteWorkflowRule.fulfilled, (state, action) => {
        state.isSaving = false;
        state.currentWorkflowRules = state.currentWorkflowRules.filter(
          r => r.id !== action.payload.id
        );
        state.successMessage = 'Rule deleted successfully';
      })
      .addCase(deleteWorkflowRule.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // ==================== Fetch Roles ====================
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.roles = action.payload.results || action.payload;
      })

      // ==================== Fetch User Groups ====================
      .addCase(fetchUserGroups.fulfilled, (state, action) => {
        state.userGroups = action.payload.results || action.payload;
      });
  },
});

// Export actions
export const {
  clearCurrentWorkflow,
  setSelectedStepId,
  setIsDirty,
  clearError,
  clearSuccessMessage,
  setSuccessMessage,
  updateLocalStep,
  addLocalStep,
  removeLocalStep,
} = workflowSlice.actions;

// Export selectors
export const selectWorkflows = (state) => state.workflow.workflows;
export const selectCurrentWorkflow = (state) => state.workflow.currentWorkflow;
export const selectCurrentWorkflowSteps = (state) => state.workflow.currentWorkflowSteps;
export const selectCurrentWorkflowRules = (state) => state.workflow.currentWorkflowRules;
export const selectWorkflowVersions = (state) => state.workflow.workflowVersions;
export const selectActiveVersion = (state) => state.workflow.activeVersion;
export const selectRoles = (state) => state.workflow.roles;
export const selectUserGroups = (state) => state.workflow.userGroups;
export const selectIsLoading = (state) => state.workflow.isLoading;
export const selectIsSaving = (state) => state.workflow.isSaving;
export const selectIsPublishing = (state) => state.workflow.isPublishing;
export const selectError = (state) => state.workflow.error;
export const selectSuccessMessage = (state) => state.workflow.successMessage;
export const selectIsDirty = (state) => state.workflow.isDirty;
export const selectSelectedStepId = (state) => state.workflow.selectedStepId;
export const selectTotalCount = (state) => state.workflow.totalCount;

// Export reducer
export default workflowSlice.reducer;
